import React, { useEffect } from 'react';
import { ocrProcessor } from '../utils/ocrUtils';
import { useOCR } from '../hooks/useOCR';
import { useNotification } from '../hooks/useNotification';
import FileUpload from './FileUpload';
import ProgressIndicator from './ProgressIndicator';
import ResultsDisplay from './ResultsDisplay';
import NotificationContainer from './NotificationContainer';

const OCRReader: React.FC = () => {
  const { result, status, selectedImage, processImage, reset, isProcessing } = useOCR();
  const { notifications, removeNotification, showSuccess, showError } = useNotification();

  useEffect(() => {
    // Initialize OCR processor when component mounts
    const initOCR = async () => {
      try {
        await ocrProcessor.initialize();
      } catch (error) {
        console.error('Failed to initialize OCR:', error);
        showError('Initialization Failed', 'Failed to initialize OCR engine. Please refresh and try again.');
      }
    };
    
    initOCR();

    // Cleanup on unmount
    return () => {
      ocrProcessor.terminate();
    };
  }, [showError]);

  // Show notifications based on results
  useEffect(() => {
    if (result.data && !result.processing) {
      showSuccess('Success!', 'NRIC data extracted successfully.');
    } else if (result.error && !result.processing) {
      showError('Processing Failed', result.error);
    }
  }, [result, showSuccess, showError]);

  const handleFileSelect = (file: File) => {
    processImage(file);
  };

  const handleReset = () => {
    reset();
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          NRIC OCR Reader
        </h1>
        <p className="text-lg text-gray-600">
          Extract name, phone number, and NRIC number from identity card images
        </p>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Upload and Preview */}
        <div className="space-y-6">
          <FileUpload 
            onFileSelect={handleFileSelect}
            disabled={isProcessing}
          />
          
          {selectedImage && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Selected Image
              </h3>
              <div className="relative">
                <img
                  src={selectedImage}
                  alt="Selected NRIC"
                  className="w-full h-auto rounded-lg border border-gray-200 shadow-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Progress and Results */}
        <div className="space-y-6">
          <ProgressIndicator status={status} />
          
          {result.error && !isProcessing && (
            <div className="card border-red-200 bg-red-50">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 text-red-500 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-red-800">
                    Processing Error
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    {result.error}
                  </p>
                  <button
                    onClick={handleReset}
                    className="mt-3 text-sm font-medium text-red-800 hover:text-red-900"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {result.data && !isProcessing && (
            <ResultsDisplay
              data={result.data}
              confidence={result.confidence}
              rawText={result.rawText}
              onReset={handleReset}
            />
          )}
        </div>
      </div>

      {/* Instructions */}
      {status.stage === 'idle' && !result.data && (
        <div className="card bg-blue-50 border-blue-200">
          <h3 className="text-lg font-medium text-blue-900 mb-3">
            How to use NRIC OCR Reader
          </h3>
          <div className="space-y-2 text-sm text-blue-800">
            <p>• Upload a clear image of an NRIC card (front side preferred)</p>
            <p>• Ensure the image is well-lit and text is clearly visible</p>
            <p>• Supported formats: JPG, PNG, GIF (max 10MB)</p>
            <p>• The system will automatically extract name, phone number, and NRIC number</p>
            <p>• Review the extracted data and export if needed</p>
          </div>
        </div>
      )}

      {/* Notifications */}
      <NotificationContainer 
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default OCRReader;
